import React, { useEffect } from 'react'
import styled from 'styled-components'
import { connect } from 'react-redux'
import LinkStyled from '../Link'
import I18n from '../I18n'
import {
  RELATION_OTHER_CONNECTION,
  RELATION_OWNERSHIP,
  RELATION_ROLE
} from '../../connectionTypes'
import SmallSpinner from '../SmallSpinner'
import Button from '../Button'
import {
  getSelectedCase,
  getRelationCases,
  isRelationCasesPending, getRelation,
  getRelationFinishedStatus
} from '../../redux/selectors'
import {
  addConnection,
  fetchUserRelationCases,
  massConfirmRelationConnections,
} from '../../redux/actions'
import AddConnectionButton from '../AddConnectionButton'
import useToggleCases from '../../hooks/useToggleCases'
import indirectIcon from '../../icons/indirect.svg'
import checkmarkIcon from '../../icons/checkmark_circled_filled.svg'
import attentionIcon from '../../icons/circled_alert_icon.svg'
import { RELATION_CASE } from '../../caseTypes'
import RelationCaseListItem from './RelationCaseListItem'

const RelationCaseList = ({
  userId,
  caseId,
  relationId,
  isRole,
  cases,
  addConnection,
  massConfirmRelationConnections,
  pending,
  relationFinishedStatus,
  fetchUserRelationCases,
  editable
}) => {

  const { toggleAllCases, selectedCaseIds, selectedCases, toggleCase, clearAllCases } =
    useToggleCases(cases)
  const buttonDisabled = selectedCaseIds.length === 0 ? 1 : 0

  const handleToggleAll = () => {
    toggleAllCases()
  }
  const onConfirm = () => {
    massConfirmRelationConnections(userId, relationId, caseId, RELATION_ROLE, selectedCaseIds)
    clearAllCases()
  }
  useEffect(() => {
    fetchUserRelationCases(userId, relationId)
  }, [])

  return (
    pending ? (
      <>
        <TitleWrapper>
          <Icon src={indirectIcon} alt="" />
          <IndirectTitle>
            <I18n id="Cases" />
          </IndirectTitle>
        </TitleWrapper>
        <IndirectLoading>
          <SmallSpinner size={'3em'} color={'secondary'} />
          <LoadingText>
            <I18n id="loading.relation_cases" />
          </LoadingText>
        </IndirectLoading>
      </>
    ) : (
      <>
        <TitleAndOptions>
          <TitleWrapper>
            <Icon src={indirectIcon} alt="" />
            <IndirectTitle>
              <I18n id="Cases" />
              {cases && ` (${cases.length})`}
            </IndirectTitle>
            <CheckedOrAttentionMarker
              src={relationFinishedStatus ? checkmarkIcon : attentionIcon}
              title={
                relationFinishedStatus ? (
                  <I18n id="Confirmed" />
                ) : (
                  <I18n id="Waiting_for_confirmation" />
                )
              }
            />
          </TitleWrapper>
          {editable && <Options>
            <ActionButton
              small
              inactive={buttonDisabled}
              disabled={buttonDisabled}
              onClick={onConfirm}
            >
              <I18n id="indirect_ownership_list.confirm_selected" />
            </ActionButton>
            <AddConnectionButton
              small
              indirect
              connectionType={RELATION_OWNERSHIP}
              ownershipType={RELATION_OWNERSHIP}
              userId={userId}
              relationId={relationId}
              hasTransientConnection={false}
              action={addConnection}
            >
              <I18n id="case_info.case_type.ownership" />
            </AddConnectionButton>
            <AddConnectionButton
              small
              indirect
              connectionType={RELATION_ROLE}
              ownershipType={RELATION_ROLE}
              userId={userId}
              relationId={relationId}
              hasTransientConnection={false}
              action={addConnection}
            >
              <I18n id="case_info.case_type.role" />

            </AddConnectionButton>
            <AddConnectionButton
              small
              indirect
              connectionType={RELATION_OTHER_CONNECTION}
              ownershipType={RELATION_OTHER_CONNECTION}
              userId={userId}
              relationId={relationId}
              hasTransientConnection={false}
              action={addConnection}
            >
              <I18n id="case_info.case_type.other" />
            </AddConnectionButton>
          </Options>
          }
        </TitleAndOptions>
        <Header>
          {editable && <ToggleAllButton onClick={handleToggleAll} to="">
            {selectedCaseIds.length === 0 ? (
              <I18n id="Select_all" />
            ) : (
              <I18n id="Remove_selection" />
            )}
          </ToggleAllButton>}
          <ColumnTitles>
            <Case>
              <I18n id="CaseId" />
            </Case>
          </ColumnTitles>
        </Header>
        <Wrapper>
          <WrapperCenter>
            <IndirectOwnershipListBox>
              {
                cases.length > 0 && (
                  <Scrollbox>
                    <List>
                      {cases
                        ?.map((item, index) => (
                          <RelationCaseListItem
                            key={caseId + item.id}
                            Case={item}
                            caseType={item.caseType}
                            selectedCases={selectedCases}
                            toggleCase={toggleCase}
                            editable={editable}
                          />
                        ))}
                    </List>
                  </Scrollbox>
                )
              }
            </IndirectOwnershipListBox>
          </WrapperCenter>
        </Wrapper>
      </>
    )
  )
}

const CheckedOrAttentionMarker = styled.img`
padding-left: 0.5em
`

const LoadingText = styled.div`
  margin-right: 2em;
`

const IndirectTitle = styled.h2`
  color: ${({ theme: { text } }) => text};
  margin-left: 1em;
`

const Header = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`

const ToggleAllButton = styled(LinkStyled)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-around;
  padding-left: 1em;
  padding-right: 1em;
`

const ColumnTitles = styled.div`
  width: 92%;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  padding-top: 0.5em;
  padding-bottom: 0.5em;
  margin-right: 2.5em;
  border-bottom: 1px solid #DCDCDC;
  color: ${({ theme: { subtleText } }) => subtleText};
`

const Case = styled.div`
  width: 11%;
`

const ActionButton = styled(Button)`
  background: #0c3689;
  border: 2px solid #0c3689;
  box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.25);
  border-radius: 25px;
  width: 10em;
`

const Options = styled.div`
  display: flex;
  justify-content: flex-start;
  padding: 0.5em;
  flex-wrap: wrap;
  max-width: ${({ theme: { maxWidth } }) => maxWidth};

  & > * {
    margin: 0 0.5em;
  }
`

const Icon = styled.img`
  margin-left: 1em;
  height: 1,7em;
  width: 1.7em;
  flex-shrink: 0;
`

const TitleAndOptions = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 0.5em;
  padding-top: 0.5em;
  padding-right: 0.5em;
`

const TitleWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding-top: 0.5em;
  padding-bottom: 0.5em;
`

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
`

const WrapperCenter = styled.div`
  width: 95%;
  height: 100%
`

const Scrollbox = styled.div`
  margin: 0;
  position: relative;
  max-height: 300px;
  padding: 0em 1em;
  overflow-y: auto;
  --scrollbarBG: rgba(0, 0, 0, 0.1);
  --thumbBG: #90a4ae;

  scrollbar-width: thin;
  scrollbar-color: var(--thumbBG) var(--scrollbarBG);
  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    background: var(--scrollbarBG);
  }
  &::-webkit-scrollbar-thumb {
    background-color: var(--thumbBG);
    border-radius: 6px;
    border: 3px solid var(--scrollbarBG);
  }
`

const List = styled.ul`
  list-style: none;
  padding-top: 0px;
  padding-left: 0px;

  & > :not(:last-child) {
    margin-bottom: 0.5em;
  }
`

const IndirectLoading = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 80%;
  width: 100%;
  gap: 2em;
  padding: 2em;
  color: ${({ theme }) => theme.secondary};
`

const IndirectOwnershipListBox = styled.div` 

`
const mapDispatchToProps = {
  addConnection,
  massConfirmRelationConnections,
  fetchUserRelationCases,
}

const mapStateToProps = (state, props) => {
  const relation = getRelation(state, getSelectedCase(state)?.id)
  const relationId = relation.id
  const caseId = relationId


  return {
    relationId: relationId,
    caseType: RELATION_CASE,
    caseId: caseId,
    cases: getRelationCases(
      state,
      relationId)
    ,
    pending: isRelationCasesPending(state, props.userId),
    relationFinishedStatus: getRelationFinishedStatus(state, props.userId, relationId)
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(RelationCaseList)